import type { PropsWithChildren } from 'react'
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { BACKGROUND_UPDATE_TIMEOUT_MS } from './constants'

interface IOrderDraftItemBackgroundUpdateStatusContextValue {
  /**
   * Reference-stable, like the clear below: both land in the order-draft event
   * subscription's handler deps, where a new identity per render would re-register the
   * subscription.
   */
  markItemsAwaitingBackgroundUpdate: (itemIds: number[]) => void
  clearItemsAwaitingBackgroundUpdate: (itemIds: number[]) => void
  /**
   * Restarts the timeout without touching the flag. For a caller that has seen the update
   * arrive but cannot clear yet: a deadline firing mid-clear would drop the flag before the
   * new value is readable. Restarts rather than cancels, so a clear that never comes is
   * still bounded.
   */
  holdItemsAwaitingBackgroundUpdate: (itemIds: number[]) => void
  /**
   * Identity changes with the state on purpose — consumers memoise item view models on it,
   * and a stable identity would leave them stale.
   */
  isItemAwaitingBackgroundUpdate: (itemId: number) => boolean
}

export const OrderDraftItemBackgroundUpdateStatusContext =
  createContext<IOrderDraftItemBackgroundUpdateStatusContextValue | null>(null)

export const OrderDraftItemBackgroundUpdateStatusContextProvider = ({
  children,
}: PropsWithChildren<{}>) => {
  const [awaitingItemIds, setAwaitingItemIds] = useState<Set<number>>(new Set())

  // Keyed by item id so an explicit clear cancels that item's timeout instead of leaving it
  // to fire against a flag that is already gone.
  const timersRef = useRef<Record<number, ReturnType<typeof setTimeout>>>({})

  const clearTimer = useCallback((itemId: number) => {
    const timer = timersRef.current[itemId]
    if (timer !== undefined) {
      clearTimeout(timer)
      delete timersRef.current[itemId]
    }
  }, [])

  const clearItemsAwaitingBackgroundUpdate = useCallback(
    (itemIds: number[]) => {
      itemIds.forEach((itemId) => clearTimer(itemId))

      setAwaitingItemIds((prev) => {
        if (!itemIds.some((itemId) => prev.has(itemId))) return prev

        const next = new Set(prev)
        itemIds.forEach((itemId) => next.delete(itemId))

        return next
      })
    },
    [clearTimer]
  )

  const armTimer = useCallback(
    (itemId: number) => {
      clearTimer(itemId)
      timersRef.current[itemId] = setTimeout(
        () => clearItemsAwaitingBackgroundUpdate([itemId]),
        BACKGROUND_UPDATE_TIMEOUT_MS
      )
    },
    [clearTimer, clearItemsAwaitingBackgroundUpdate]
  )

  const holdItemsAwaitingBackgroundUpdate = useCallback(
    (itemIds: number[]) => {
      itemIds.forEach((itemId) => armTimer(itemId))
    },
    [armTimer]
  )

  const markItemsAwaitingBackgroundUpdate = useCallback(
    (itemIds: number[]) => {
      itemIds.forEach((itemId) => armTimer(itemId))

      setAwaitingItemIds((prev) => {
        if (itemIds.every((itemId) => prev.has(itemId))) return prev

        const next = new Set(prev)
        itemIds.forEach((itemId) => next.add(itemId))

        return next
      })
    },
    [armTimer]
  )

  const isItemAwaitingBackgroundUpdate = useCallback(
    (itemId: number) => awaitingItemIds.has(itemId),
    [awaitingItemIds]
  )

  useEffect(
    () => () => {
      Object.values(timersRef.current).forEach(clearTimeout)
      timersRef.current = {}
    },
    []
  )

  const contextValue = useMemo(
    () => ({
      markItemsAwaitingBackgroundUpdate,
      clearItemsAwaitingBackgroundUpdate,
      holdItemsAwaitingBackgroundUpdate,
      isItemAwaitingBackgroundUpdate,
    }),
    [
      markItemsAwaitingBackgroundUpdate,
      clearItemsAwaitingBackgroundUpdate,
      holdItemsAwaitingBackgroundUpdate,
      isItemAwaitingBackgroundUpdate,
    ]
  )

  return (
    <OrderDraftItemBackgroundUpdateStatusContext.Provider value={contextValue}>
      {children}
    </OrderDraftItemBackgroundUpdateStatusContext.Provider>
  )
}
